/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import classnames from 'classnames';
import { action, computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy';

import { IAutoSuggestOptions } from '@msdyn365-commerce-modules/address';
import { AutoSuggest } from './common/address-autosuggest';
import { AddressCommon } from './common/address-common';
import { AddressFormat } from './common/address-format';
import { AddressItemType } from './common/address-format.data';
import { AddressMetaData } from './common/address-meta-data';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from './common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from './common/components/address-add';
import { AddressSelect, IAddressSelectProps } from './common/components/address-select';
import { AddressShow, IAddressShowProps } from './common/components/address-show';
import { ICustomCheckoutShippingAddressData } from './custom-checkout-shipping-address.data';
import { ICustomCheckoutShippingAddressProps } from './custom-checkout-shipping-address.props.autogenerated';

export interface ICheckoutAddressProps extends ICustomCheckoutShippingAddressProps<ICustomCheckoutShippingAddressData>, IModuleStateProps {}

export interface ICheckoutShippingAddressViewState {
    isShowAddress: boolean;
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface ICheckoutShippingAddressViewProps extends ICheckoutAddressProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutShippingAddressViewState;
    CheckoutShippingAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddress: IAddressShowProps;
    showAddressSelect: IAddressSelectProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component
 * @extends {React.Component<ICustomCheckoutShippingAddressProps<ICustomCheckoutShippingAddressData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutShippingAddress extends React.Component<ICheckoutAddressProps> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private addUpdateAddress: Address;
    @observable private countryRegionId: string = 'USA';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;
    @observable private addressListSelectedAddress: Address = {};

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private defaultAddressType: number = 6; // Default to Home
    private telemetryContent?: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    private multiplePickupStoreSwitchName: string = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    private retailMultiplePickUpOptionEnabled?: boolean = false;

    constructor(props: ICheckoutAddressProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.addUpdateAddress = {};
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );
        this.validationError = {};
        this.retailMultiplePickUpOptionEnabled = data?.featureState?.result?.find(
            feature => feature.Name === this.multiplePickupStoreSwitchName
        )?.IsEnabled;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions }
        } = this.props;

        try {
            await this.props.data.checkout;
            this._setDefaultCountryRegionId();
            await this._initModuleState();
        } catch (error) {
            this.props.context.telemetry.error('Failed to initialize checkout data:', error);
        }

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            async () => {
                try {
                    await this._getStateProvinces();
                } catch (error) {
                    this.props.context.telemetry.error('Failed to get state provinces on operation change:', error);
                }
            }
        );

        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(
                telemetry,
                options,
                channel?.BingMapsApiKey,
                channel?.ChannelCountryRegionISOCode,
                channel?.DefaultLanguageId
            );

            // Customer doesn't have any address. Then add view will be loaded directly. Code for the same to handle that
            if (
                this.props.data.storeSelectorStateManager.result &&
                (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
            ) {
                await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
            }

            reaction(
                () =>
                    this.props.data.storeSelectorStateManager.result?.loadMapApi &&
                    (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
                async () => {
                    await this._attachMapAutoSuggest();
                }
            );
        }
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    public render(): JSX.Element | null {
        if (!this._canShip()) {
            return null;
        }

        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            addressListSelectedAddress: this.addressListSelectedAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddress: this.currentOperation === AddressOperation.Show && this.selectedAddress,
                isShowAddresList: this.currentOperation === AddressOperation.List && this.customerAddresses.length > 0,
                isShowAddOrUpdateAddress:
                    this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update
            },
            CheckoutShippingAddress: {
                moduleProps: this.props,
                className: classnames('ms-checkout-shipping-address', config.className)
            },
            showAddress: this._renderShowAddress(),
            showAddressSelect: this._renderSelectAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        const addressFormat =
            this.currentOperation === AddressOperation.Add && this.addressCommon.isAuthenticatedFlow()
                ? this.addressFormat
                : this.addressFormat;
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            hasError: this.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            onSave: this.onSubmit,
            // tslint:disable-next-line: no-empty
            onCancel: !this.selectedAddress && !this.addressCommon.isAuthenticatedFlow() ? () => {} : this._resetView
        });
    };

    private _renderSelectAddress = (): IAddressSelectProps => {
        return AddressSelect({
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            resources: this.resources,
            addressPurposes: this.addressPurposes,
            selectedAddress: this.addressListSelectedAddress,
            onAddressOptionChange: this._onAddressOptionChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            telemetryContent: this.telemetryContent,
            onAddAddress: this._goToAddAddress,
            onSave: this._onSelectAddress,
            // tslint:disable-next-line: no-empty
            onCancel: !this.selectedAddress ? () => {} : this._resetView
        });
    };

    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({
                address: this.selectedAddress,
                addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || ''),
                addressPurposes: this.addressPurposes
            });
        }

        return null;
    }

    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    };

    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        }, 0);
    };

    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(
            this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        );
        addressFormatItem.forEach(format => {
            // @ts-ignore
            if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                // @ts-ignore
                this.addressFormat[format.name] = '';
            }
        });
        this._clearValidation();
    };

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    };

    private _onAddressOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const addressRecordId = event.currentTarget.value;
        const selectedAddress = this.customerAddresses.find(address => (address.RecordId || '').toString() === addressRecordId);
        if (selectedAddress) {
            this.addressListSelectedAddress = selectedAddress;
        }
    };

    private onAddressAddUpdate = (name: string, value: string | boolean) => {
        set(this.addUpdateAddress, { [name]: value });

        if (name === 'Phone' && typeof value === 'string') {
            if (!this._validatePhoneNumber(value)) {
                set(this.validationError, { ['Phone']: this.resources.addressPhoneRequiredErrorMessage });
            } else {
                set(this.validationError, { ['Phone']: null });
            }
        }

        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _validatePhoneNumber = (phone?: string): boolean => {
        if (!phone?.trim()?.length) {
            return false;
        }

        const phonePattern = /^(\+)?[0-9]+$/;
        if (!phonePattern.test(phone.trim())) {
            return false;
        }

        const digitsOnly = phone.replace(/\+/g, '');
        if (digitsOnly.length < 6) {
            return false;
        }

        return true;
    };

    private _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            return;
        }

        if (!this._validatePhoneNumber(this.addUpdateAddress.Phone)) {
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : Promise.resolve({ address: this.addUpdateAddress });
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this.isUpdating = true;
        this._updateModuleState();

        response.then(
            async (result: IAddressResponse) => {
                this.isUpdating = false;

                if (result.address) {
                    this.hasError = false;
                    try {
                        await this._onAddOrUpdateSuccess(result);
                    } catch (error) {
                        this.hasError = true;
                        this.props.context.telemetry.error('Failed to process address update:', error);
                        this._updateModuleState();
                    }
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                    this._updateModuleState();
                }
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
                this._updateModuleState();
            }
        );
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, {
            ['ThreeLetterISORegionName']: countryRegionId
        });
        set(this.addUpdateAddress, {
            ['TwoLetterISORegionName']: twoLetterISORegionName
        });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    };

    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    };

    private async _getStateProvinces(): Promise<void> {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        try {
            const result = await this.addressCommon.getStateProvinces(this.countryRegionId);
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { ['State']: '' });
            }

            this.stateProvinceInfo = result;
        } catch (error) {
            this.props.context.telemetry.error('Failed to get state provinces:', error);
        }
    }

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;
        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _getDefaultAddress = (): Address | undefined => {
        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
        }
        return undefined;
    };

    private _initModuleState = async (): Promise<void> => {
        this.props.moduleState.init({
            status: this._canShip() ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        if (this._canShip()) {
            const defaultAddress = this._getShippingAddressFromCartLines() || this._getDefaultAddress();
            if (defaultAddress && !this.addressCommon.isEmpty(defaultAddress)) {
                if (!this._validatePhoneNumber(defaultAddress.Phone)) {
                    set(this.validationError, { ['Phone']: this.resources.addressPhoneRequiredErrorMessage });
                    this._updateCurrentOperation(AddressOperation.Update, defaultAddress);
                } else {
                    this._updateCurrentOperation(AddressOperation.Show, defaultAddress);
                    try {
                        await this._setShippingAddress(defaultAddress);
                        this._updateModuleState();
                    } catch (error) {
                        this.props.context.telemetry.error('Failed to set shipping address:', error);
                    }
                }
            } else {
                this._updateCurrentOperation(AddressOperation.Add);
            }
        }
    };

    private onSubmit = async (): Promise<void> => {
        try {
            switch (this.currentOperation) {
                case AddressOperation.Add:
                case AddressOperation.Update:
                    this._onAddressAddUpdateSubmit();
                    break;
                case AddressOperation.List:
                    await this._onSelectAddress();
                    break;
                default:
                    this.props.telemetry.error('Invalid operation');
            }
        } catch (error) {
            this.props.context.telemetry.error('Error during submit operation:', error);
        }
    };

    private onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._clearAddressFields();
                if (!(!this.selectedAddress && !this.addressCommon.isAuthenticatedFlow())) {
                    this._resetView();
                }

                break;
            case AddressOperation.List:
                if (this.selectedAddress) {
                    this._resetView();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onEdit = (): void => {
        if (this.addressCommon.isAuthenticatedFlow() && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.List, this.shippingAddress);
        } else if (this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.shippingAddress);
        }

        this._updateModuleState();
    };

    private _getShippingAddressFromCartLines = (): Address | undefined => {
        const { request } = this.props.context;
        const { channelDeliveryOptionConfig } = this.props.data;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        const cartLines = this.retailMultiplePickUpOptionEnabled
            ? ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );

        if (cartLines && cartLines.length > 0) {
            return cartLines[0].ShippingAddress;
        }

        return undefined;
    };

    private _canShip = (): boolean => {
        const { checkout, channelDeliveryOptionConfig } = this.props.data;
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        if (!checkout.result || !request.channel || checkout.result.checkoutCart.isEmpty || checkout.result.checkoutCart.hasInvoiceLine) {
            return false;
        }

        return !!(this.retailMultiplePickUpOptionEnabled
            ? checkout?.result?.checkoutCart?.cart?.CartLines?.some(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              ) ?? false
            : checkout?.result?.checkoutCart?.cart?.CartLines?.some(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              ) ?? false);
    };

    private _onSelectAddress = async () => {
        if (!this._validatePhoneNumber(this.addressListSelectedAddress.Phone)) {
            set(this.validationError, { ['Phone']: this.resources.addressPhoneRequiredErrorMessage });

            this._updateCurrentOperation(AddressOperation.Update, this.addressListSelectedAddress);
            this._updateModuleState();
            return;
        }

        this._clearValidation();

        this._updateCurrentOperation(AddressOperation.Show, this.addressListSelectedAddress);

        if (this.props.config.multipleAddressShippingEnabled) {
            this._setCartLinesShippingAddress(this.addressListSelectedAddress); // ✅ Use line-level logic
        } else {
            await this._setShippingAddress(this.addressListSelectedAddress); // single-address fallback
        }

        this._updateModuleState();
    };

    private _goToAddAddress = () => {
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = async (response: IAddressResponse) => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }

        if (response.address) {
            if (!this._validatePhoneNumber(response.address.Phone)) {
                set(this.validationError, { ['Phone']: this.resources.addressPhoneRequiredErrorMessage });
                this._updateCurrentOperation(AddressOperation.Update, response.address);
                this._updateModuleState();
                return;
            }

            this._clearValidation();

            this._updateCurrentOperation(AddressOperation.Show, response.address);
            try {
                await this._setShippingAddress(response.address);
            } catch (error) {
                this.props.context.telemetry.error('Failed to set shipping address:', error);
            }
            this._updateModuleState();
        }
    };

    private _setShippingAddress = async (address: Address): Promise<void> => {
        if (this.props.data.checkout.result) {
            const newShippingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newShippingAddress.TwoLetterISORegionName) {
                newShippingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }

            try {
                await this.props.data.checkout.result.updateShippingAddress({
                    newShippingAddress
                });
            } catch (error) {
                this.props.context.telemetry.error('Failed to update shipping address:', error);
            }
        }
    };

    private _setCartLinesShippingAddress = (address: Address): void => {
        if (this.props.data.checkout.result) {
            const cartLines = this.props.data.checkout.result.checkoutCart.cart.CartLines ?? [];
            const updatedCartLines = cartLines.map(line => ({
                ...line,
                ShippingAddress: address
            }));
            this.props.data.checkout.result.checkoutCart
                .updateCartLineShippingAddress({
                    updatedCartLinesObject: updatedCartLines
                })
                .catch(error => {
                    this.props.telemetry.error('Failed to update cart line shipping address:', error);
                });
        }
    };

    @computed get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress;
    }

    private _resetView = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._updateCurrentOperation(
                    this.addressCommon.isAuthenticatedFlow() ? AddressOperation.List : AddressOperation.Show,
                    this.shippingAddress
                );
                break;
            default:
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        }
        this._updateModuleState();
    };

    private _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = this.selectedAddress ? { ...this.selectedAddress } : {};
            set(this.addUpdateAddress, {
                ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            });
            set(this.addUpdateAddress, {
                ['AddressTypeValue']: this.addUpdateAddress.AddressTypeValue || this.defaultAddressType
            });
        } else if (this.currentOperation === AddressOperation.List) {
            this.addressListSelectedAddress = this.selectedAddress ? { ...this.selectedAddress } : {};
        }
    };

    private _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
        } else if (this.isUpdating) {
            this.props.moduleState.onPending();
        } else {
            this.props.moduleState.onUpdating();
        }
    };
}

export default CheckoutShippingAddress;
